<template>
  <div class="zone-management-container">
    <!-- Header -->
    <div class="zone-header">
      <div class="header-content">
        <div class="title-section">
          <h1 class="page-title">
            <b-icon icon="map" class="mr-2"></b-icon>
            Delivery Zone Management
          </h1>
          <p class="page-subtitle">Create and manage delivery zones using interactive maps</p>
        </div>
        
        <div class="header-actions">
          <b-button 
            :variant="isDrawing ? 'danger' : 'primary'" 
            @click="toggleDrawingMode"
            class="action-btn"
          >
            <b-icon :icon="isDrawing ? 'x-circle' : 'pencil-square'" class="mr-2"></b-icon>
            {{ isDrawing ? 'Cancel Drawing' : 'Draw New Zone' }}
          </b-button>
          
          <b-button variant="outline-secondary" @click="refreshZones" class="action-btn">
            <b-icon icon="arrow-clockwise" class="mr-2"></b-icon>
            Refresh
          </b-button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="zone-content">
      <!-- Sidebar - Zone List -->
      <div class="zone-sidebar" :class="{ 'collapsed': sidebarCollapsed }">
        <div class="sidebar-header">
          <h5 class="mb-0">
            <b-icon icon="list-ul" class="mr-2"></b-icon>
            Zones ({{ zones.length }})
          </h5>
          <b-button 
            variant="link" 
            size="sm" 
            @click="sidebarCollapsed = !sidebarCollapsed"
            class="collapse-btn"
          >
            <b-icon :icon="sidebarCollapsed ? 'chevron-right' : 'chevron-left'"></b-icon>
          </b-button>
        </div>

        <div class="sidebar-search">
          <b-form-input 
            v-model="searchQuery" 
            placeholder="Search zones..." 
            size="sm"
            class="search-input"
          >
          </b-form-input>
        </div>

        <div class="zone-list">
          <div 
            v-for="zone in filteredZones" 
            :key="zone.id"
            class="zone-list-item"
            :class="{ 
              'active': selectedZone && selectedZone.id === zone.id,
              'inactive': !zone.is_active 
            }"
            @click="selectZone(zone)"
          >
            <div class="zone-color" :style="{ background: zone.color }"></div>
            <div class="zone-info">
              <div class="zone-name">{{ zone.name }}</div>
              <div class="zone-code">{{ zone.code }}</div>
              <div class="zone-stats">
                <b-badge variant="light" size="sm">
                  {{ zone.order_count || 0 }} orders
                </b-badge>
              </div>
            </div>
            <div class="zone-actions">
              <b-button 
                variant="outline-primary" 
                size="sm" 
                @click.stop="editZone(zone)"
                title="Edit"
              >
                <b-icon icon="pencil"></b-icon>
              </b-button>
              <b-button 
                variant="outline-danger" 
                size="sm" 
                @click.stop="confirmDeleteZone(zone)"
                title="Delete"
              >
                <b-icon icon="trash"></b-icon>
              </b-button>
            </div>
          </div>

          <div v-if="filteredZones.length === 0" class="empty-zones">
            <b-icon icon="map" font-scale="3" class="text-muted"></b-icon>
            <p class="text-muted mt-2">No zones found</p>
            <b-button variant="primary" size="sm" @click="toggleDrawingMode">
              <b-icon icon="plus" class="mr-1"></b-icon>
              Create First Zone
            </b-button>
          </div>
        </div>
      </div>

      <!-- Map Container -->
      <div class="map-wrapper">
        <div id="zoneMap" class="zone-map"></div>
        
        <!-- Map Controls -->
        <div class="map-controls">
          <b-button-group vertical>
            <b-button variant="light" size="sm" @click="zoomIn" title="Zoom In">
              <b-icon icon="plus"></b-icon>
            </b-button>
            <b-button variant="light" size="sm" @click="zoomOut" title="Zoom Out">
              <b-icon icon="dash"></b-icon>
            </b-button>
            <b-button variant="light" size="sm" @click="fitAllZones" title="Fit All Zones">
              <b-icon icon="arrows-fullscreen"></b-icon>
            </b-button>
            <b-button 
              variant="light" 
              size="sm" 
              @click="toggleSatellite" 
              :class="{ 'active': isSatellite }"
              title="Toggle Satellite"
            >
              <b-icon icon="layers"></b-icon>
            </b-button>
          </b-button-group>
        </div>

        <!-- Drawing Instructions Overlay -->
        <div v-if="isDrawing" class="drawing-instructions">
          <b-icon icon="info-circle" class="mr-2"></b-icon>
          Click on the map to start drawing the zone boundary. Click the first point to complete.
          <b-button variant="link" size="sm" @click="cancelDrawing">Cancel</b-button>
        </div>

        <!-- Selected Zone Info Panel -->
        <div v-if="selectedZone && !isDrawing" class="zone-info-panel">
          <div class="info-header" :style="{ background: selectedZone.color }">
            <h5 class="mb-0">{{ selectedZone.name }}</h5>
            <b-button variant="link" size="sm" class="text-white" @click="selectedZone = null">
              <b-icon icon="x"></b-icon>
            </b-button>
          </div>
          <div class="info-body">
            <div class="info-row">
              <span class="label">Code:</span>
              <span class="value">{{ selectedZone.code }}</span>
            </div>
            <div class="info-row">
              <span class="label">Status:</span>
              <b-badge :variant="selectedZone.is_active ? 'success' : 'secondary'">
                {{ selectedZone.is_active ? 'Active' : 'Inactive' }}
              </b-badge>
            </div>
            <div class="info-row">
              <span class="label">Orders:</span>
              <span class="value">{{ selectedZone.order_count || 0 }}</span>
            </div>
            <div class="info-row">
              <span class="label">Revenue:</span>
              <span class="value">${{ formatNumber(selectedZone.total_revenue || 0) }}</span>
            </div>
            <div v-if="selectedZone.description" class="info-row">
              <span class="label">Description:</span>
              <span class="value">{{ selectedZone.description }}</span>
            </div>
          </div>
        </div>

        <!-- Loading Overlay -->
        <div v-if="loading" class="loading-overlay">
          <div class="spinner-border text-primary" role="status"></div>
          <p class="mt-2">Loading zones...</p>
        </div>
      </div>
    </div>

    <!-- Zone Form Modal -->
    <b-modal 
      id="zone-form-modal" 
      :title="editingZone ? 'Edit Zone' : 'Create New Zone'"
      size="lg"
      @hidden="resetForm"
      no-close-on-backdrop
    >
      <div class="zone-form">
        <b-form @submit.prevent="saveZone">
          <b-row>
            <b-col md="6">
              <b-form-group label="Zone Name *" label-for="zone-name">
                <b-form-input 
                  id="zone-name" 
                  v-model="form.name" 
                  required
                  placeholder="e.g., Zone A - Philadelphia North"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Zone Code *" label-for="zone-code">
                <b-form-input 
                  id="zone-code" 
                  v-model="form.code" 
                  required
                  placeholder="e.g., PHLN"
                  maxlength="20"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-form-group label="Color" label-for="zone-color">
                <div class="color-picker-wrapper">
                  <b-form-input 
                    id="zone-color" 
                    v-model="form.color" 
                    type="color"
                    class="color-input"
                  ></b-form-input>
                  <div class="color-presets">
                    <span 
                      v-for="color in colorPresets" 
                      :key="color"
                      class="color-preset"
                      :style="{ background: color }"
                      :class="{ 'active': form.color === color }"
                      @click="form.color = color"
                    ></span>
                  </div>
                </div>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Priority" label-for="zone-priority">
                <b-form-input 
                  id="zone-priority" 
                  v-model.number="form.priority" 
                  type="number"
                  min="0"
                  placeholder="Higher = more priority"
                ></b-form-input>
                <small class="text-muted">Used when zones overlap</small>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Description" label-for="zone-description">
            <b-form-textarea 
              id="zone-description" 
              v-model="form.description" 
              rows="2"
              placeholder="Optional description of this zone"
            ></b-form-textarea>
          </b-form-group>

          <b-form-group>
            <b-form-checkbox v-model="form.is_active" switch>
              Active Zone
            </b-form-checkbox>
          </b-form-group>

          <!-- Boundary Info -->
          <div class="boundary-info">
            <div class="d-flex justify-content-between align-items-center">
              <span>
                <b-icon icon="bounding-box" class="mr-2"></b-icon>
                Boundary Points: {{ form.boundaries ? form.boundaries.length : 0 }}
              </span>
              <b-button 
                v-if="form.boundaries && form.boundaries.length >= 3"
                variant="outline-info" 
                size="sm"
                @click="previewZoneOnMap"
              >
                <b-icon icon="eye" class="mr-1"></b-icon>
                Preview
              </b-button>
            </div>
            <div v-if="!form.boundaries || form.boundaries.length < 3" class="text-warning mt-2">
              <b-icon icon="exclamation-triangle" class="mr-1"></b-icon>
              Please draw a zone boundary on the map (minimum 3 points)
            </div>
          </div>
        </b-form>

        <!-- Geocoding Helper -->
        <hr>
        <div class="geocoding-helper">
          <h6>
            <b-icon icon="search" class="mr-2"></b-icon>
            Search Address to Center Map
          </h6>
          <b-input-group size="sm">
            <b-form-input 
              v-model="geocodeQuery" 
              placeholder="Enter address, city, or zip code..."
              @keyup.enter="geocodeAddress"
            ></b-form-input>
            <b-input-group-append>
              <b-button variant="primary" @click="geocodeAddress" :disabled="geocoding">
                <b-spinner v-if="geocoding" small></b-spinner>
                <b-icon v-else icon="search"></b-icon>
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <small class="text-muted">This will center the map to help you draw the zone</small>
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="secondary" @click="$bvModal.hide('zone-form-modal')">
          Cancel
        </b-button>
        <b-button 
          variant="primary" 
          @click="saveZone"
          :disabled="saving || !form.boundaries || form.boundaries.length < 3"
        >
          <b-spinner v-if="saving" small class="mr-2"></b-spinner>
          {{ editingZone ? 'Update Zone' : 'Create Zone' }}
        </b-button>
      </template>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal id="delete-zone-modal" title="Delete Zone" centered>
      <p>Are you sure you want to delete <strong>{{ zoneToDelete ? zoneToDelete.name : '' }}</strong>?</p>
      <p class="text-danger mb-0">
        <b-icon icon="exclamation-triangle" class="mr-1"></b-icon>
        This action cannot be undone. Orders in this zone will not be affected.
      </p>
      <template #modal-footer>
        <b-button variant="secondary" @click="$bvModal.hide('delete-zone-modal')">
          Cancel
        </b-button>
        <b-button variant="danger" @click="deleteZone" :disabled="deleting">
          <b-spinner v-if="deleting" small class="mr-2"></b-spinner>
          Delete Zone
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import Requestor from '@/utilities/Requestor';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

export default {
  name: 'ZoneManagement',
  
  data() {
    return {
      // Map
      map: null,
      drawnItems: null,
      drawControl: null,
      streetLayer: null,
      satelliteLayer: null,
      zoneLayers: {},
      isSatellite: false,
      
      // Data
      zones: [],
      selectedZone: null,
      loading: false,
      saving: false,
      deleting: false,
      
      // UI State
      isDrawing: false,
      sidebarCollapsed: false,
      searchQuery: '',
      
      // Forms
      form: this.getEmptyForm(),
      editingZone: null,
      zoneToDelete: null,
      
      // Geocoding
      geocodeQuery: '',
      geocoding: false,
      
      // Color presets
      colorPresets: [
        '#ff8080', '#80ff80', '#8080ff', '#ffff80', 
        '#ff80ff', '#80ffff', '#ffb366', '#b366ff',
        '#ff6666', '#66ff66', '#6666ff', '#ffcc00'
      ],
      
      // Default map center (Philadelphia area)
      defaultCenter: [39.9526, -75.1652],
      defaultZoom: 11,
      
      // Internal flags
      mapInitialized: false
    };
  },
  
  computed: {
    filteredZones() {
      if (!this.searchQuery) return this.zones;
      const query = this.searchQuery.toLowerCase();
      return this.zones.filter(z => 
        (z.name || '').toLowerCase().includes(query) ||
        (z.code || '').toLowerCase().includes(query)
      );
    },
    
    packet() {
      try {
        const p = JSON.parse(localStorage.packet || '{}');
        if (!p.userDetails) {
          try {
            p.userDetails = JSON.parse(localStorage.userDetails || '{}');
          } catch (e) {
            p.userDetails = {};
          }
        }
        return p;
      } catch (e) {
        return { userDetails: {} };
      }
    }
  },
  
  mounted() {
    // Delay init so the tab has time to become visible
    this.$nextTick(() => {
      setTimeout(() => {
        this.initMap();
        this.fetchZones();
      }, 300);
    });
  },
  
  activated() {
    // Fires when the tab becomes visible again (keep-alive)
    this.$nextTick(() => {
      setTimeout(() => {
        if (!this.map || !this.mapInitialized) {
          this.initMap();
        } else {
          this.map.invalidateSize();
          if (this.zones.length > 0) {
            this.renderZonesOnMap();
          }
        }
      }, 250);
    });
  },
  
  beforeDestroy() {
    this.destroyMap();
  },
  
  methods: {
    // ==========================================
    // UTILITIES
    // ==========================================
    getEmptyForm() {
      return {
        id: null,
        name: '',
        code: '',
        color: '#ff8080',
        description: '',
        priority: 0,
        is_active: true,
        boundaries: [],
        company_id: 1
      };
    },
    
    getUserContext() {
      const p = this.packet || {};
      const u = p.userDetails || {};
      return {
        user_id: u.id || null,
        username: u.username || 'SYSTEM'
      };
    },
    
    destroyMap() {
      if (this.map) {
        try {
          this.map.remove();
        } catch (e) {
          console.warn('Error removing map:', e);
        }
        this.map = null;
        this.mapInitialized = false;
      }
    },
    
    // ==========================================
    // MAP INITIALIZATION
    // ==========================================
    initMap() {
      const mapElement = document.getElementById('zoneMap');
      if (!mapElement) {
        // Element not ready yet, retry shortly
        setTimeout(() => {
          if (!this.mapInitialized) this.initMap();
        }, 300);
        return;
      }
      
      // If already initialized, just resize
      if (this.map && this.mapInitialized) {
        setTimeout(() => this.map.invalidateSize(), 100);
        return;
      }
      
      // Clean up existing map
      this.destroyMap();
      
      try {
        // Initialize map
        this.map = L.map('zoneMap', {
          center: this.defaultCenter,
          zoom: this.defaultZoom,
          zoomControl: false
        });
        
        // Add tile layers
        this.streetLayer = L.tileLayer(
          'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          {
            attribution: '&copy; OpenStreetMap contributors',
            maxZoom: 19
          }
        );
        
        this.satelliteLayer = L.tileLayer(
          'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          {
            attribution: '&copy; Esri',
            maxZoom: 19
          }
        );
        
        this.streetLayer.addTo(this.map);
        
        // Initialize drawn items layer
        this.drawnItems = new L.FeatureGroup();
        this.map.addLayer(this.drawnItems);
        
        // Initialize draw control
        this.drawControl = new L.Control.Draw({
          position: 'topright',
          draw: {
            polygon: {
              allowIntersection: false,
              drawError: {
                color: '#e53e3e',
                message: '<strong>Error:</strong> Shape edges cannot cross!'
              },
              shapeOptions: {
                color: '#667eea',
                weight: 3,
                opacity: 0.8,
                fillOpacity: 0.3
              }
            },
            polyline: false,
            rectangle: false,
            circle: false,
            circlemarker: false,
            marker: false
          },
          edit: {
            featureGroup: this.drawnItems,
            remove: true
          }
        });
        
        // Handle draw events
        this.map.on(L.Draw.Event.CREATED, this.onDrawCreated);
        
        // Click on map to deselect zone
        this.map.on('click', () => {
          if (!this.isDrawing) {
            this.selectedZone = null;
          }
        });
        
        this.mapInitialized = true;
        
        // Force size recalculation (fixes blank map when tab was hidden)
        setTimeout(() => {
          if (this.map) {
            this.map.invalidateSize();
            if (this.zones.length > 0) {
              this.renderZonesOnMap();
            }
          }
        }, 300);
      } catch (e) {
        console.error('Error initializing map:', e);
      }
    },
    
    toggleDrawingMode() {
      if (!this.mapInitialized || !this.map) {
        this.$bvToast.toast('Map is not ready yet. Please wait.', {
          title: 'Not Ready',
          variant: 'warning',
          autoHideDelay: 2000
        });
        return;
      }
      
      if (this.isDrawing) {
        this.cancelDrawing();
        return;
      }
      
      // Clear existing drawn items
      this.drawnItems.clearLayers();
      
      // Reset form
      this.form = this.getEmptyForm();
      this.editingZone = null;
      
      // Add draw control
      if (!this.map.hasLayer(this.drawControl)) {
        this.map.addControl(this.drawControl);
      }
      
      // Enable polygon drawing
      new L.Draw.Polygon(this.map, this.drawControl.options.draw.polygon).enable();
      
      this.isDrawing = true;
      this.selectedZone = null;
      
      // Dim existing zone layers
      Object.values(this.zoneLayers).forEach(layer => {
        layer.setStyle({ opacity: 0.3, fillOpacity: 0.1 });
      });
    },
    
    cancelDrawing() {
      this.isDrawing = false;
      if (this.drawnItems) this.drawnItems.clearLayers();
      
      if (this.map && this.map.hasLayer(this.drawControl)) {
        this.map.removeControl(this.drawControl);
      }
      
      // Restore zone layers
      Object.values(this.zoneLayers).forEach(layer => {
        layer.setStyle({ opacity: 0.8, fillOpacity: 0.3 });
      });
      
      this.form = this.getEmptyForm();
    },
    
    onDrawCreated(e) {
      const layer = e.layer;
      const latLngs = layer.getLatLngs()[0];
      
      const boundaries = latLngs.map(point => ({
        lat: point.lat,
        lng: point.lng
      }));
      
      this.drawnItems.clearLayers();
      this.drawnItems.addLayer(layer);
      
      this.form.boundaries = boundaries;
      
      this.isDrawing = false;
      if (this.map && this.map.hasLayer(this.drawControl)) {
        this.map.removeControl(this.drawControl);
      }
      
      this.$bvModal.show('zone-form-modal');
    },
    
    zoomIn() {
      if (this.map) this.map.zoomIn();
    },
    
    zoomOut() {
      if (this.map) this.map.zoomOut();
    },
    
    fitAllZones() {
      if (!this.map || this.zones.length === 0) return;
      
      const bounds = L.latLngBounds([]);
      Object.values(this.zoneLayers).forEach(layer => {
        bounds.extend(layer.getBounds());
      });
      
      if (bounds.isValid()) {
        this.map.fitBounds(bounds, { padding: [50, 50] });
      }
    },
    
    toggleSatellite() {
      if (!this.map) return;
      
      if (this.isSatellite) {
        this.map.removeLayer(this.satelliteLayer);
        this.map.addLayer(this.streetLayer);
      } else {
        this.map.removeLayer(this.streetLayer);
        this.map.addLayer(this.satelliteLayer);
      }
      this.isSatellite = !this.isSatellite;
    },
    
    // ==========================================
    // API CALLS
    // ==========================================
    fetchZones() {
      this.loading = true;
      
      const companyId = this.form.company_id || 1;
      const path = `/api/zones/list_zones/?company_id=${companyId}`;
      const data = {};
      const that = this;
      
      const success = (response) => {
        if (response.data && response.data.error) {
          that.$bvToast.toast(response.data.error, {
            title: 'Error',
            variant: 'warning',
            autoHideDelay: 3000
          });
        } else {
          that.zones = (response.data && response.data.result) || [];
          that.renderZonesOnMap();
        }
        that.loading = false;
      };
      
      const failure = (error) => {
        console.error('Error fetching zones:', error);
        that.$bvToast.toast('Failed to load zones', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 3000
        });
        that.loading = false;
      };
      
      Requestor.getSyncP(path, data, this, success, failure);
    },
    
    saveZone() {
      if (!this.form.name || !this.form.code) {
        this.$bvToast.toast('Name and Code are required', {
          title: 'Validation Error',
          variant: 'warning',
          autoHideDelay: 3000
        });
        return;
      }
      
      if (!this.form.boundaries || this.form.boundaries.length < 3) {
        this.$bvToast.toast('Please draw a zone boundary with at least 3 points', {
          title: 'Validation Error',
          variant: 'warning',
          autoHideDelay: 3000
        });
        return;
      }
      
      this.saving = true;
      
      const polygon = this.boundariesToWKT(this.form.boundaries);
      const userCtx = this.getUserContext();
      
      const path = this.editingZone 
        ? '/api/zones/update_zone/' 
        : '/api/zones/create_zone/';
      
      const data = {
        id: this.form.id,
        company_id: this.form.company_id,
        name: this.form.name.trim(),
        code: this.form.code.trim().toUpperCase(),
        color: this.form.color,
        description: this.form.description,
        priority: this.form.priority,
        is_active: this.form.is_active,
        boundaries: polygon,
        user_id: userCtx.user_id,
        username: userCtx.username
      };
      
      const that = this;
      
      const success = (response) => {
        if (response.data && response.data.error) {
          that.$bvToast.toast(response.data.error, {
            title: 'Error',
            variant: 'warning',
            autoHideDelay: 3000
          });
        } else {
          that.$bvToast.toast(
            that.editingZone ? 'Zone updated successfully' : 'Zone created successfully',
            {
              title: 'Success',
              variant: 'success',
              autoHideDelay: 2000
            }
          );
          that.$bvModal.hide('zone-form-modal');
          that.resetForm();
          that.fetchZones();
        }
        that.saving = false;
      };
      
      const failure = (error) => {
        console.error('Error saving zone:', error);
        that.$bvToast.toast('Failed to save zone', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 3000
        });
        that.saving = false;
      };
      
      Requestor.postSyncP(path, data, this, success, failure);
    },
    
    editZone(zone) {
      this.editingZone = zone;
      this.form = {
        id: zone.id,
        name: zone.name,
        code: zone.code,
        color: zone.color || '#ff8080',
        description: zone.description || '',
        priority: zone.priority || 0,
        is_active: zone.is_active !== false,
        boundaries: this.parseBoundaries(zone.boundaries),
        company_id: zone.company_id
      };
      
      this.showZoneOnMap(zone);
      this.$bvModal.show('zone-form-modal');
    },
    
    confirmDeleteZone(zone) {
      this.zoneToDelete = zone;
      this.$bvModal.show('delete-zone-modal');
    },
    
    deleteZone() {
      if (!this.zoneToDelete) return;
      
      this.deleting = true;
      
      const userCtx = this.getUserContext();
      const path = '/api/zones/delete_zone/';
      const data = { 
        id: this.zoneToDelete.id,
        user_id: userCtx.user_id,
        username: userCtx.username
      };
      const that = this;
      
      const success = (response) => {
        if (response.data && response.data.error) {
          that.$bvToast.toast(response.data.error, {
            title: 'Error',
            variant: 'warning',
            autoHideDelay: 3000
          });
        } else {
          that.$bvToast.toast('Zone deleted successfully', {
            title: 'Success',
            variant: 'success',
            autoHideDelay: 2000
          });
          that.$bvModal.hide('delete-zone-modal');
          
          if (that.zoneLayers[that.zoneToDelete.id]) {
            that.map.removeLayer(that.zoneLayers[that.zoneToDelete.id]);
            delete that.zoneLayers[that.zoneToDelete.id];
          }
          
          if (that.selectedZone && that.selectedZone.id === that.zoneToDelete.id) {
            that.selectedZone = null;
          }
          
          that.zoneToDelete = null;
          that.fetchZones();
        }
        that.deleting = false;
      };
      
      const failure = (error) => {
        console.error('Error deleting zone:', error);
        that.$bvToast.toast('Failed to delete zone', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 3000
        });
        that.deleting = false;
      };
      
      Requestor.postSyncP(path, data, this, success, failure);
    },
    
    // ==========================================
    // MAP RENDERING
    // ==========================================
    renderZonesOnMap() {
      if (!this.map || !this.mapInitialized) return;
      
      // Clear existing zone layers
      Object.values(this.zoneLayers).forEach(layer => {
        this.map.removeLayer(layer);
      });
      this.zoneLayers = {};
      
      this.zones.forEach(zone => {
        const boundaries = this.parseBoundaries(zone.boundaries);
        if (boundaries.length < 3) return;
        
        const latLngs = boundaries.map(b => [b.lat, b.lng]);
        
        const polygon = L.polygon(latLngs, {
          color: zone.color,
          weight: 3,
          opacity: 0.8,
          fillColor: zone.color,
          fillOpacity: 0.3,
          className: `zone-polygon zone-${zone.id}`
        });
        
        polygon.bindTooltip(`
          <div style="font-weight: 600;">${zone.name}</div>
          <div style="font-size: 11px; color: #666;">${zone.code}</div>
        `, { sticky: true });
        
        polygon.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          this.selectZone(zone);
        });
        
        polygon.addTo(this.map);
        this.zoneLayers[zone.id] = polygon;
      });
    },
    
    selectZone(zone) {
      if (!zone) return;
      this.selectedZone = zone;
      
      Object.entries(this.zoneLayers).forEach(([id, layer]) => {
        if (parseInt(id) === zone.id) {
          layer.setStyle({ weight: 4, opacity: 1, fillOpacity: 0.5 });
          layer.bringToFront();
        } else {
          layer.setStyle({ weight: 3, opacity: 0.8, fillOpacity: 0.3 });
        }
      });
      
      const layer = this.zoneLayers[zone.id];
      if (layer && this.map) {
        this.map.fitBounds(layer.getBounds(), { padding: [50, 50] });
      }
    },
    
    showZoneOnMap(zone) {
      if (!this.map || !this.drawnItems) return;
      
      this.drawnItems.clearLayers();
      
      const boundaries = this.parseBoundaries(zone.boundaries);
      if (boundaries.length >= 3) {
        const latLngs = boundaries.map(b => [b.lat, b.lng]);
        const polygon = L.polygon(latLngs, {
          color: zone.color,
          weight: 3,
          opacity: 0.8,
          fillColor: zone.color,
          fillOpacity: 0.3
        });
        
        this.drawnItems.addLayer(polygon);
        this.map.fitBounds(polygon.getBounds(), { padding: [50, 50] });
      }
    },
    
    previewZoneOnMap() {
      if (!this.form.boundaries || this.form.boundaries.length < 3) return;
      if (!this.map || !this.drawnItems) return;
      
      this.drawnItems.clearLayers();
      
      const latLngs = this.form.boundaries.map(b => [b.lat, b.lng]);
      const polygon = L.polygon(latLngs, {
        color: this.form.color,
        weight: 3,
        opacity: 0.8,
        fillColor: this.form.color,
        fillOpacity: 0.3
      });
      
      this.drawnItems.addLayer(polygon);
      this.map.fitBounds(polygon.getBounds(), { padding: [50, 50] });
    },
    
    // ==========================================
    // HELPERS
    // ==========================================
    boundariesToWKT(boundaries) {
      if (!boundaries || boundaries.length < 3) return null;
      
      const points = [...boundaries];
      if (points[0].lat !== points[points.length - 1].lat || 
          points[0].lng !== points[points.length - 1].lng) {
        points.push(points[0]);
      }
      
      const coords = points.map(p => `${p.lng} ${p.lat}`).join(', ');
      return `POLYGON((${coords}))`;
    },
    
    parseBoundaries(boundaries) {
      if (!boundaries) return [];
      
      if (Array.isArray(boundaries)) {
        return boundaries;
      }
      
      if (typeof boundaries === 'string' && boundaries.startsWith('POLYGON')) {
        try {
          const coordsStr = boundaries
            .replace('POLYGON((', '')
            .replace('))', '');
          
          const coords = coordsStr.split(',').map(pair => {
            const [lng, lat] = pair.trim().split(' ').map(Number);
            return { lat, lng };
          });
          
          if (coords.length > 1) {
            const first = coords[0];
            const last = coords[coords.length - 1];
            if (first.lat === last.lat && first.lng === last.lng) {
              coords.pop();
            }
          }
          
          return coords;
        } catch (e) {
          console.error('Error parsing WKT:', e);
          return [];
        }
      }
      
      return [];
    },
    
    resetForm() {
      this.form = this.getEmptyForm();
      this.editingZone = null;
      this.geocodeQuery = '';
      if (this.drawnItems) this.drawnItems.clearLayers();
      
      Object.values(this.zoneLayers).forEach(layer => {
        layer.setStyle({ weight: 3, opacity: 0.8, fillOpacity: 0.3 });
      });
    },
    
    formatNumber(num) {
      return Number(num).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },
    
    refreshZones() {
      this.fetchZones();
      this.$bvToast.toast('Zones refreshed', {
        title: 'Success',
        variant: 'success',
        autoHideDelay: 1500
      });
    },
    
    // ==========================================
    // GEOCODING
    // ==========================================
    async geocodeAddress() {
  console.log('[Geocode] ===== START =====');
  console.log('[Geocode] Query:', JSON.stringify(this.geocodeQuery));
  
  if (!this.geocodeQuery || !this.geocodeQuery.trim()) {
    console.log('[Geocode] Empty query, aborting');
    return;
  }
  
  this.geocoding = true;
  
  const query = this.geocodeQuery.trim();
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`;
  
  console.log('[Geocode] URL:', url);
  console.log('[Geocode] Map exists:', !!this.map);
  console.log('[Geocode] Map initialized:', this.mapInitialized);
  
  try {
    console.log('[Geocode] Fetching...');
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    });
    
    console.log('[Geocode] Response status:', response.status);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('[Geocode] Data received:', data);
    console.log('[Geocode] Data length:', data ? data.length : 0);
    
    if (!Array.isArray(data) || data.length === 0) {
      console.log('[Geocode] No results');
      this.$bvToast.toast('No results found for: ' + query, {
        title: 'Not Found',
        variant: 'warning',
        autoHideDelay: 3000
      });
      this.geocoding = false;
      return;
    }
    
    const result = data[0];
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    
    console.log('[Geocode] Parsed coords:', lat, lng);
    
    if (isNaN(lat) || isNaN(lng)) {
      throw new Error('Invalid coordinates from geocoder');
    }
    
    // Try to center map — but never let it throw
    try {
      if (this.map && this.mapInitialized) {
        console.log('[Geocode] Centering map...');
        this.map.setView([lat, lng], 14);
        setTimeout(() => {
          try { this.map.invalidateSize(); } catch (e) { console.warn(e); }
        }, 200);
        console.log('[Geocode] Map centered OK');
      } else {
        console.warn('[Geocode] Map not ready, skipping setView');
      }
    } catch (mapErr) {
      console.error('[Geocode] Map error (non-fatal):', mapErr);
    }
    
    // Try to show toast — but never let it throw
    try {
      this.$bvToast.toast('Centered on: ' + (result.display_name || query), {
        title: 'Location Found',
        variant: 'success',
        autoHideDelay: 3000
      });
      console.log('[Geocode] Toast shown OK');
    } catch (toastErr) {
      console.error('[Geocode] Toast error (non-fatal):', toastErr);
    }
    
    console.log('[Geocode] ===== SUCCESS =====');
    
  } catch (error) {
    console.error('[Geocode] ===== FATAL ERROR =====');
    console.error('[Geocode] Error object:', error);
    console.error('[Geocode] Error message:', error && error.message);
    console.error('[Geocode] Error stack:', error && error.stack);
    
    try {
      this.$bvToast.toast(
        (error && error.message) || 'Error searching address',
        {
          title: 'Search Error',
          variant: 'danger',
          autoHideDelay: 3000
        }
      );
    } catch (toastErr) {
      console.error('[Geocode] Toast failed too:', toastErr);
    }
  } finally {
    this.geocoding = false;
    console.log('[Geocode] ===== END =====');
  }
}
  }
};
</script>

<style scoped>
.zone-management-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f8f9fa;
}

.zone-header {
  background: white;
  padding: 20px 25px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  z-index: 10;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
}

.page-subtitle {
  color: #718096;
  margin: 5px 0 0;
  font-size: 0.9rem;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  font-weight: 600;
  border-radius: 8px;
}

.zone-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Sidebar */
.zone-sidebar {
  width: 320px;
  background: white;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
}

.zone-sidebar.collapsed {
  width: 50px;
}

.zone-sidebar.collapsed .sidebar-header h5,
.zone-sidebar.collapsed .sidebar-search,
.zone-sidebar.collapsed .zone-list {
  display: none;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #e2e8f0;
}

.sidebar-search {
  padding: 10px 15px;
  border-bottom: 1px solid #e2e8f0;
}

.search-input {
  border-radius: 8px;
}

.zone-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.zone-list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 8px;
  border: 2px solid transparent;
}

.zone-list-item:hover {
  background: #f7fafc;
}

.zone-list-item.active {
  background: #ebf8ff;
  border-color: #4299e1;
}

.zone-list-item.inactive {
  opacity: 0.6;
}

.zone-color {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  flex-shrink: 0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.zone-info {
  flex: 1;
  min-width: 0;
}

.zone-name {
  font-weight: 600;
  color: #2d3748;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.zone-code {
  font-size: 0.75rem;
  color: #718096;
  font-family: monospace;
}

.zone-stats {
  margin-top: 4px;
}

.zone-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.empty-zones {
  text-align: center;
  padding: 40px 20px;
}

/* Map */
.map-wrapper {
  flex: 1;
  position: relative;
}

.zone-map {
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.map-controls {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 1000;
}

.map-controls .btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
}

.map-controls .btn.active {
  background: #667eea;
  color: white;
}

.drawing-instructions {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(45, 55, 72, 0.95);
  color: white;
  padding: 12px 20px;
  border-radius: 10px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.zone-info-panel {
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 280px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 1000;
}

.info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  color: white;
}

.info-body {
  padding: 15px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row .label {
  color: #718096;
  font-size: 0.85rem;
}

.info-row .value {
  font-weight: 600;
  color: #2d3748;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

/* Form Styles */
.zone-form {
  padding: 10px 0;
}

.color-picker-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.color-input {
  height: 40px;
  padding: 2px;
  cursor: pointer;
}

.color-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.color-preset {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s ease;
}

.color-preset:hover {
  transform: scale(1.1);
}

.color-preset.active {
  border-color: #2d3748;
  box-shadow: 0 0 0 2px white, 0 0 0 4px #2d3748;
}

.boundary-info {
  background: #f7fafc;
  padding: 15px;
  border-radius: 8px;
  margin-top: 15px;
}

.geocoding-helper {
  margin-top: 15px;
}

/* Responsive */
@media (max-width: 768px) {
  .zone-sidebar {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 1500;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }
  
  .zone-sidebar:not(.collapsed) {
    transform: translateX(0);
  }
  
  .header-content {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
  
  .zone-info-panel {
    left: 20px;
    right: 20px;
    width: auto;
  }
}
</style>

<style>
/* Leaflet Draw customizations - global (must not be scoped) */
.leaflet-draw-toolbar {
  margin-top: 10px !important;
}

.leaflet-draw-toolbar a {
  background-color: white !important;
  border-radius: 4px !important;
}

.leaflet-draw-actions {
  background: #2d3748 !important;
}

.leaflet-draw-actions a {
  color: white !important;
}

/* Fix leaflet container to ensure proper sizing inside tabs */
.leaflet-container {
  width: 100%;
  height: 100%;
  background: #e5e5e5;
}
</style>


// WEBPACK FOOTER //
// src/components/ControlPanel/ZoneManagement.vue